import { StyleProvider } from '@ant-design/cssinjs';
import { App as AntdApp, ConfigProvider } from 'antd';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { antdThemeFor } from './antdTheme';
import { DASHBOARD_THEME_STORAGE_KEY, applyUiThemeTokens, type ThemeMode, type UiPaletteName } from './tokens';

/**
 * ⚠️ [LIGHT-DEFAULT] THE KEY IS THE SHARED ONE, NOT A SECOND COPY OF THE LITERAL. The pre-paint
 * boot script in `client-core/theme/tokensCss.ts` reads what this file writes, and a rename that
 * moved only one of them would show up as a flash of the wrong theme and nothing else.
 */
const STORAGE_KEY = DASHBOARD_THEME_STORAGE_KEY;

/**
 * ⚠️ [amended LIGHT-COLOURFUL] THE DASHBOARD'S ONE PALETTE.
 *
 * [C1h] made the light palette a system-wide switch between `standard` and `colourful`, cached
 * per browser in `pams.uimode`. Colourful is now the only light palette for everybody, so there
 * is no switch, no cache and no revalidation: a browser that cached `standard` gets colourful
 * because nothing reads that key any more. Dark mode is unchanged — `uiPalettes.colourful.dark`
 * IS the standard dark object.
 *
 * Typed `UiPaletteName` rather than `as const` so `antdThemeFor` and `applyUiThemeTokens` keep
 * their palette parameter honest instead of being narrowed to one literal.
 */
export const DASHBOARD_PALETTE: UiPaletteName = 'colourful';

/**
 * ⚠️ [amended LIGHT-DEFAULT] LIGHT IS THE DEFAULT, AND THE OS IS NEVER CONSULTED.
 *
 * A browser with no stored choice renders LIGHT — on a dark laptop too. `prefers-color-scheme` was
 * read in exactly THREE places for this app and all three went in one pass: the seed below, the
 * media-query LISTENER that used to follow the OS until the operator chose, and the pre-paint boot
 * script in `client-core/theme/tokensCss.ts`. ⚠️ The two FIRST-PAINT readers had to move together —
 * leaving either would make the document head and the mounted app disagree on the same load.
 *
 * ⚠️ IT IS A CONSTANT RATHER THAN AN INLINE `'light'`, so the one thing a reader has to find to
 * change this default is named, and `ThemeProvider.test.tsx` asserts it against a dark OS.
 */
const DEFAULT_MODE: ThemeMode = 'light';

type ThemeContextValue = {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const readStoredMode = (): ThemeMode | undefined => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : undefined;
  } catch {
    // Private browsing, or storage disabled. The default applies for this session.
    return undefined;
  }
};

/**
 * Theme state plus the AntD providers, in the order that makes the three styling
 * systems coexist (CLAUDE.md §8).
 *
 * `StyleProvider layer` is the load-bearing part: it tells AntD's cssinjs runtime to
 * emit its styles inside `@layer antd`, which `styles/index.css` orders between
 * Tailwind's preflight and Tailwind's utilities. The result is:
 *
 *     preflight  <  AntD  <  Tailwind utilities
 *
 * so AntD components survive the CSS reset, and a Tailwind utility still overrides an
 * AntD component when you put one on it. Without this prop, AntD's styles land outside
 * every layer — and unlayered CSS beats ALL layered CSS regardless of order, so
 * Tailwind utilities would silently stop working on AntD components.
 *
 * Preflight stays ON. shadcn/ui depends on it; disabling it to fix the tables is how
 * you break the shell instead.
 */
export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode() ?? DEFAULT_MODE);

  /*
   * Applied synchronously against the DOM so the first paint is already themed.
   *
   * ⚠️ [C1h §2] IT MUST BE `applyUiThemeTokens`, NOT `applyThemeTokens`. These are INLINE
   * custom properties on <html>, and an inline style beats every selector. `applyThemeTokens`
   * is bound to the standard palette, so calling it here would repaint the colourful page the
   * pre-paint block painted back to monochrome a few milliseconds after load.
   */
  useEffect(() => {
    applyUiThemeTokens(DASHBOARD_PALETTE, mode);
  }, [mode]);

  /*
   * ⚠️ THERE IS NO MEDIA-QUERY LISTENER, AND ITS ABSENCE IS THE FEATURE. It used to follow the
   * OS until the operator chose for themselves, so a laptop switching to dark at sunset
   * repainted a dashboard nobody had asked to repaint. The theme changes when somebody presses
   * the toggle, and at no other moment.
   */
  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage unavailable: the choice still applies for this session.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, setMode, toggle: () => setMode(mode === 'dark' ? 'light' : 'dark') }),
    [mode, setMode],
  );

  return (
    <ThemeContext value={value}>
      <StyleProvider layer>
        <ConfigProvider theme={antdThemeFor(mode, DASHBOARD_PALETTE)}>
          <AntdApp>{children}</AntdApp>
        </ConfigProvider>
      </StyleProvider>
    </ThemeContext>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = use(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside <ThemeProvider>');
  return context;
};
